import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Send, PackagePlus, PackageCheck, Truck, CheckCircle2, Timer,
  RefreshCw, Search, ChevronRight, ChevronDown, ExternalLink, TrendingUp,
  Users, BarChart3, Sparkles, AlertTriangle, Clock, Package2, Settings, X, Tag,
  Archive,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  getExpediereToday, listExpediereShipments, getExpediereStats,
  getExpediereSettings, updateExpediereSettings, getExpediereAvailableTags,
} from "@/lib/expediere";
import { fmtShort, fmtRelative } from "@/lib/format";

const REFRESH_MS = 60_000;

const STATE_META = {
  de_pregatit:     { label: "De pregătit",       cls: "border-slate-300 bg-slate-50 text-slate-700",       Icon: PackagePlus },
  pregatit:        { label: "Pregătit",          cls: "border-sky-300 bg-sky-50 text-sky-700",             Icon: PackageCheck },
  partial_ridicat: { label: "Parțial ridicat",   cls: "border-violet-300 bg-violet-50 text-violet-700",    Icon: Package2 },
  ridicat_azi:     { label: "Ridicat azi",       cls: "border-emerald-300 bg-emerald-50 text-emerald-700", Icon: Truck },
  nescanate_vechi: { label: "Nescanat vechi",    cls: "border-zinc-300 bg-zinc-50 text-zinc-700",          Icon: Archive },
};

function money(v, ccy = "RON") {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;
}

// ============================== PAGE ==============================
export default function ExpedierePage() {
  const [tab, setTab] = useState("today");
  return (
    <div className="space-y-4" data-testid="expediere-page">
      <PageHeader />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-secondary/40">
          <TabsTrigger value="today"  data-testid="exp-tab-today">Astăzi</TabsTrigger>
          <TabsTrigger value="list"   data-testid="exp-tab-list">Listă operațională</TabsTrigger>
          <TabsTrigger value="stats"  data-testid="exp-tab-stats">Statistici</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-5"><TodayCards /></TabsContent>
        <TabsContent value="list"  className="mt-5"><OperationalList /></TabsContent>
        <TabsContent value="stats" className="mt-5"><StatisticsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Send className="h-7 w-7 text-primary" /> Expediere
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Vizibilitate în timp real asupra fluxului zilei. Cifrele afișează
          <span className="font-semibold text-foreground"> comenzi distincte</span>{" "}
          (unitate principală), cu <span className="font-semibold text-foreground">colete (parcele/AWB-uri)</span>{" "}
          ca dimensiune secundară — o comandă poate avea mai multe colete. Refresh la 60s.
        </p>
      </div>
    </div>
  );
}

// ============================== TAB 1 · Astăzi ==============================
function TodayCards() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await getExpediereToday();
      setData(r);
    } catch (_err) { /* silent */ } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) return <Skeleton className="h-40 w-full" />;

  const dp = data?.de_pregatit || {};
  const dpConfigured = dp.configured;
  const dpLabel = dpConfigured
    ? `tag${(dp.tags || []).length > 1 ? "-uri" : ""} «${(dp.tags || []).join(" · ")}» · ultimele ${dp.period_days || 7} zile`
    : "neconfigurat — apasă ⚙";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* Card 1: De pregătit — configurable */}
        <ConfigurableKpiCard
          testid="exp-kpi-de_pregatit"
          label="Comenzi de pregătit"
          primary={dpConfigured ? dp.orders : null}
          secondary={dpLabel}
          configured={dpConfigured}
          Icon={PackagePlus}
          color="text-slate-700"
          onConfigure={() => setSettingsOpen(true)}
        />
        <BigKpiCard
          testid="exp-kpi-pregatite_azi"
          label="Pregătite azi"
          primary={data?.pregatite_azi?.orders}
          secondary={`${data?.pregatite_azi?.parcels ?? 0} colete`}
          Icon={PackageCheck}
          color="text-sky-700"
        />
        <BigKpiCard
          testid="exp-kpi-asteapta_curierul"
          label="Așteaptă curierul"
          primary={data?.asteapta_curierul?.parcels}
          primaryUnit="colete"
          secondary={`${data?.asteapta_curierul?.orders ?? 0} comenzi · fereastră ${data?.asteapta_curierul?.period_days || 7}z`}
          subtitle={data?.asteapta_curierul?.parcels_over_24h > 0 ? `${data.asteapta_curierul.parcels_over_24h} colete peste 24h` : null}
          extra={data?.asteapta_curierul?.orders_partial > 0 ? `${data.asteapta_curierul.orders_partial} parțial ridicate` : null}
          Icon={Timer}
          color="text-amber-700"
          urgent={(data?.asteapta_curierul?.parcels_over_24h || 0) > 0}
        />
        <BigKpiCard
          testid="exp-kpi-ridicate_azi"
          label="Ridicate azi"
          primary={data?.ridicate_azi?.orders}
          secondary={`${data?.ridicate_azi?.parcels ?? 0} colete scanate azi`}
          subtitle="numai comenzi cu toate coletele scanate"
          Icon={Truck}
          color="text-emerald-700"
        />
        <BigKpiCard
          testid="exp-kpi-onorate_azi"
          label="Comenzi onorate azi"
          primary={data?.onorate_azi?.orders}
          secondary={money(data?.onorate_azi?.value || 0, data?.onorate_azi?.currency || "RON")}
          subtitle={`${data?.onorate_azi?.parcels ?? 0} colete`}
          Icon={CheckCircle2}
          color="text-primary"
        />
      </div>

      {/* Nescanate vechi surfacing */}
      {(data?.asteapta_curierul?.orders_nescanate_vechi || 0) > 0 && (
        <Card className="p-3 bg-zinc-50 border-zinc-200 flex items-center gap-3" data-testid="exp-nescanate-vechi-notice">
          <Archive className="h-4 w-4 text-zinc-600" />
          <div className="text-xs flex-1">
            <span className="font-semibold text-zinc-800">{data.asteapta_curierul.orders_nescanate_vechi} comenzi</span>{" "}
            <span className="text-muted-foreground">
              cu {data.asteapta_curierul.parcels_nescanate_vechi} colete etichetate acum mai mult de 7 zile fără scan GLS — probabil anomalii.
            </span>
          </div>
          <Link to="/expediere?tab=list&state=nescanate_vechi" className="text-xs text-primary hover:underline">Vezi lista →</Link>
        </Card>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${refreshing ? "bg-emerald-500 animate-pulse" : "bg-emerald-500"}`} />
          Actualizat {data?.as_of ? fmtRelative(data.as_of) : ""} · refresh 60s · {data?.timezone || "Europe/Bucharest"}
        </div>
        <Button variant="ghost" size="sm" onClick={() => load()} disabled={refreshing} data-testid="exp-today-refresh" className="h-7">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Reîmprospătează
        </Button>
      </div>

      <ReconcileHelper data={data} />

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={() => { setSettingsOpen(false); load(); }}
      />
    </div>
  );
}

function ConfigurableKpiCard({ label, primary, secondary, configured, Icon, color, onConfigure, testid }) {
  return (
    <Card className="p-5 hover:shadow-md transition-shadow relative" data-testid={testid}>
      <button
        type="button"
        onClick={onConfigure}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        title="Configurează"
        data-testid="exp-kpi-de_pregatit-settings"
      >
        <Settings className="h-4 w-4" />
      </button>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold pr-6">{label}</div>
      {!configured ? (
        <div className="mt-3 space-y-2">
          <div className="font-display font-bold text-2xl text-muted-foreground/50">—</div>
          <Button size="sm" variant="outline" onClick={onConfigure} className="h-7 text-[11px]" data-testid="exp-kpi-de_pregatit-configure-btn">
            <Settings className="h-3 w-3 mr-1" /> Configurează tag-uri
          </Button>
        </div>
      ) : (
        <>
          <div className={`font-display font-bold text-4xl mt-2 tabular-nums flex items-baseline gap-1 ${color}`}>
            {primary ?? 0}
            <span className="text-[10px] font-normal text-muted-foreground tracking-wider uppercase">comenzi</span>
          </div>
          {secondary && (
            <div className="text-[10px] text-muted-foreground mt-1 truncate" title={secondary}>{secondary}</div>
          )}
        </>
      )}
      <div className="absolute bottom-1.5 right-3">
        <Icon className={`h-5 w-5 ${color} opacity-40`} />
      </div>
    </Card>
  );
}

function BigKpiCard({ label, primary, primaryUnit = "comenzi", secondary, subtitle, extra, Icon, color, urgent, loading, testid }) {
  return (
    <Card
      className={`p-5 hover:shadow-md transition-shadow ${urgent ? "border-rose-300 bg-rose-50/40" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
        <Icon className={`h-5 w-5 ${urgent ? "text-rose-500" : color}`} />
      </div>
      {loading ? (
        <Skeleton className="h-9 w-16 mt-2" />
      ) : (
        <>
          <div className={`font-display font-bold text-4xl mt-2 tabular-nums flex items-baseline gap-1 ${urgent ? "text-rose-700" : color}`}>
            {primary ?? 0}
            <span className="text-[10px] font-normal text-muted-foreground tracking-wider uppercase">{primaryUnit}</span>
          </div>
          {secondary && (
            <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">
              {secondary}
            </div>
          )}
        </>
      )}
      {subtitle && (
        <div className={`text-[10px] mt-1.5 ${urgent ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>
          {subtitle}
        </div>
      )}
      {extra && (
        <div className="text-[10px] mt-0.5 text-violet-700 font-semibold">
          {extra}
        </div>
      )}
    </Card>
  );
}

function SettingsDialog({ open, onClose, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [tags, setTags] = useState([]);
  const [periodDays, setPeriodDays] = useState(7);
  const [available, setAvailable] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([getExpediereSettings(), getExpediereAvailableTags(200)])
      .then(([s, av]) => {
        setSettings(s);
        setTags(s?.de_pregatit?.tags || []);
        setPeriodDays(s?.de_pregatit?.period_days || 7);
        setAvailable(av || []);
      })
      .catch(() => toast.error("Nu am putut încărca setările"))
      .finally(() => setLoading(false));
  }, [open]);

  const filteredAvailable = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return available.filter((t) => !norm || t.tag.toLowerCase().includes(norm));
  }, [available, q]);

  const toggleTag = (t) => {
    setTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateExpediereSettings({ tags, period_days: periodDays });
      toast.success("Setări salvate");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu am putut salva");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="exp-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings className="h-4 w-4" /> Configurează „Comenzi de pregătit"
          </DialogTitle>
          <DialogDescription className="text-xs">
            Filtrează backlog-ul de comenzi de coletare după tag-uri Shopify (multi-select) și o fereastră temporală. Setările se persistă și cardul le respectă imediat.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Perioadă</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min={1} max={90}
                value={periodDays}
                onChange={(e) => setPeriodDays(parseInt(e.target.value || "7", 10))}
                className="h-8 w-24 text-sm"
                data-testid="exp-settings-period-input"
              />
              <span className="text-xs text-muted-foreground">zile (comenzi create în ultimele X zile)</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Tag-uri Shopify</label>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">
              Comenzile se includ dacă au ORICARE din tag-urile selectate.
            </p>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2" data-testid="exp-settings-selected-tags">
                {tags.map((t) => (
                  <Badge key={t} variant="outline" className="border-primary/30 bg-primary/10 text-primary text-[11px]">
                    <Tag className="h-2.5 w-2.5 mr-1" /> {t}
                    <button type="button" onClick={() => toggleTag(t)} className="ml-1 hover:text-primary/70">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex items-center gap-1 mb-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Caută în tag-urile existente în Shopify…"
                className="h-8 text-xs"
                data-testid="exp-settings-tag-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border border-border rounded" data-testid="exp-settings-tag-list">
              {loading ? (
                <div className="p-3"><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-3/4" /></div>
              ) : filteredAvailable.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground text-center">Nici un tag găsit.</div>
              ) : (
                filteredAvailable.map((t) => {
                  const selected = tags.includes(t.tag);
                  return (
                    <button
                      key={t.tag}
                      type="button"
                      onClick={() => toggleTag(t.tag)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs hover:bg-secondary/40 border-b border-border last:border-b-0 ${selected ? "bg-primary/5" : ""}`}
                      data-testid={`exp-settings-tag-${t.tag.replace(/\s+/g, "_")}`}
                    >
                      <span className="flex items-center gap-2">
                        <input type="checkbox" checked={selected} readOnly className="rounded border-border" />
                        <span className="font-mono">{t.tag}</span>
                      </span>
                      <span className="text-[10px] text-muted-foreground">{t.count}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="exp-settings-cancel">Anulează</Button>
          <Button onClick={save} disabled={saving} data-testid="exp-settings-save">
            {saving ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconcileHelper({ data }) {
  if (!data) return null;
  const parcelsPrepared = data.pregatite_azi?.parcels ?? 0;
  const parcelsPickedToday = data.ridicate_azi?.parcels ?? 0;
  const parcelsStillWaiting = Math.max(0, parcelsPrepared - parcelsPickedToday);

  const ordersPrepared = data.pregatite_azi?.orders ?? 0;
  const ordersFullyPicked = data.ridicate_azi?.orders ?? 0;
  const ordersPartial = data.asteapta_curierul?.orders_partial ?? 0;

  return (
    <Card className="p-4 bg-secondary/30 border-dashed" data-testid="exp-reconcile-card">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" /> Verificare consistență (Europe/Bucharest)
      </div>
      <div className="text-sm text-muted-foreground space-y-1">
        <div>
          <span className="font-semibold text-foreground">Colete pregătite azi:</span>{" "}
          <span className="font-semibold text-foreground">{parcelsPrepared}</span> = ridicate azi{" "}
          <span className="font-semibold text-emerald-700">{parcelsPickedToday}</span> + rămase pe raft{" "}
          <span className="font-semibold text-amber-700">{parcelsStillWaiting}</span>{" "}
          {parcelsPrepared === (parcelsPickedToday + parcelsStillWaiting) && <span className="text-emerald-600">✓</span>}
        </div>
        <div>
          <span className="font-semibold text-foreground">Comenzi pregătite azi:</span>{" "}
          <span className="font-semibold text-foreground">{ordersPrepared}</span>, dintre care{" "}
          <span className="font-semibold text-emerald-700">{ordersFullyPicked}</span> ridicate complet
          {ordersPartial > 0 && (
            <> · <span className="font-semibold text-violet-700">{ordersPartial}</span> parțial (colete plecate + colete pe raft)</>
          )}.
        </div>
      </div>
    </Card>
  );
}

// ============================== TAB 2 · Listă ==============================
function OperationalList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [countsOrders, setCountsOrders] = useState({});
  const [countsParcels, setCountsParcels] = useState({});
  const [state, setState] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("state") || "all";
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { limit: 300 };
      if (state !== "all") params.state = state;
      if (search.trim()) params.search = search.trim();
      const r = await listExpediereShipments(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setCountsOrders(r.counts_orders || {});
      setCountsParcels(r.counts_parcels || {});
    } catch (_err) { /* silent */ } finally { setLoading(false); }
  }, [state, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const stateChips = useMemo(() => ([
    { key: "all",             label: "Toate",             ordersCount: total,                               parcelsCount: null },
    { key: "de_pregatit",     label: "De pregătit",       ordersCount: countsOrders.de_pregatit || 0,       parcelsCount: null },
    { key: "pregatit",        label: "Pregătite",         ordersCount: countsOrders.pregatit || 0,          parcelsCount: countsParcels.pregatit || 0 },
    { key: "partial_ridicat", label: "Parțial ridicate",  ordersCount: countsOrders.partial_ridicat || 0,   parcelsCount: countsParcels.partial_ridicat || 0 },
    { key: "ridicat_azi",     label: "Ridicate azi",      ordersCount: countsOrders.ridicat_azi || 0,       parcelsCount: countsParcels.ridicat_azi || 0 },
    { key: "nescanate_vechi", label: "Nescanate vechi",   ordersCount: countsOrders.nescanate_vechi || 0,   parcelsCount: countsParcels.nescanate_vechi || 0 },
  ]), [total, countsOrders, countsParcels]);

  return (
    <div className="space-y-3" data-testid="exp-list-tab">
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {stateChips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setState(c.key)}
              className={`px-3 py-1 rounded-full text-xs border transition ${
                state === c.key ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary/40 border-border"
              }`}
              data-testid={`exp-list-chip-${c.key}`}
            >
              {c.label} · {c.ordersCount}
              {c.parcelsCount != null && c.parcelsCount !== c.ordersCount && (
                <span className="ml-1 opacity-70">({c.parcelsCount}c)</span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto flex-1 min-w-[200px] max-w-[320px]">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută comandă / AWB / client / telefon / județ…"
            className="h-8 text-xs"
            data-testid="exp-list-search"
          />
        </div>
        <Button variant="ghost" size="sm" onClick={() => load()} data-testid="exp-list-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2">Comandă</th>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Județ</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Colete</th>
              <th className="px-3 py-2">Vechime</th>
              <th className="px-3 py-2">Ora pregătirii</th>
              <th className="px-3 py-2 text-right">→</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && items.length === 0 ? Array.from({ length: 5 }).map((_, i) => (
              <tr key={i}><td colSpan={10}><Skeleton className="h-4 w-full m-2" /></td></tr>
            )) : items.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-muted-foreground" data-testid="exp-list-empty">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
                  Fără comenzi active în acest filtru.
                </td>
              </tr>
            ) : items.map((it, i) => <OrderRow key={`${it.order_key}-${i}`} it={it} />)}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function OrderRow({ it }) {
  const [expanded, setExpanded] = useState(false);
  const meta = STATE_META[it.state] || STATE_META.pregatit;
  const urgent = (it.max_waiting_hours || 0) >= 24;
  const isMulti = (it.total_parcels || 0) > 1;
  const canExpand = isMulti && it.parcels?.length > 0;

  return (
    <>
      <tr
        className={`hover:bg-secondary/30 ${urgent ? "bg-rose-50/50" : ""}`}
        data-testid={`exp-list-row-${it.order_key}`}
      >
        <td className="px-3 py-2">
          {canExpand ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-muted-foreground hover:text-foreground"
              data-testid={`exp-row-expand-${it.order_key}`}
              aria-label="Extinde colete"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : null}
        </td>
        <td className="px-3 py-2 font-mono font-semibold">
          {it.order_number || "—"}
        </td>
        <td className="px-3 py-2">
          <div className="max-w-[180px] truncate" title={it.customer_name}>{it.customer_name || "—"}</div>
          {it.customer_phone && <div className="text-[10px] text-muted-foreground font-mono">{it.customer_phone}</div>}
        </td>
        <td className="px-3 py-2 text-muted-foreground">{it.county || "—"}</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {it.cod_amount != null ? (
            <div className="font-semibold">{money(it.cod_amount, it.currency)}</div>
          ) : it.total != null ? (
            <div className="text-muted-foreground">{money(it.total, it.currency)}</div>
          ) : "—"}
          {it.cod_amount != null && <div className="text-[9px] uppercase text-amber-700">COD</div>}
        </td>
        <td className="px-3 py-2">
          <Badge variant="outline" className={`text-[10px] border ${meta.cls}`}>
            <meta.Icon className="h-3 w-3 mr-1 inline" /> {meta.label}
          </Badge>
        </td>
        <td className="px-3 py-2 tabular-nums">
          {it.total_parcels > 0 ? (
            <span className={`inline-flex items-center gap-1 font-medium ${isMulti ? "text-violet-700" : ""}`}>
              {isMulti && <Package2 className="h-3 w-3" />}
              {it.picked_parcels}/{it.total_parcels}
              {isMulti && <span className="text-[9px] uppercase text-muted-foreground">colete</span>}
            </span>
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </td>
        <td className={`px-3 py-2 tabular-nums ${urgent ? "text-rose-700 font-semibold" : it.max_waiting_hours ? "text-amber-700" : "text-muted-foreground"}`}>
          {it.max_waiting_hours != null ? (
            <span className="inline-flex items-center gap-1">
              {urgent && <AlertTriangle className="h-3 w-3" />}
              {it.max_waiting_hours < 24 ? `${it.max_waiting_hours}h` : `${Math.floor(it.max_waiting_hours / 24)}z ${Math.round(it.max_waiting_hours % 24)}h`}
            </span>
          ) : "—"}
        </td>
        <td className="px-3 py-2 text-muted-foreground text-[11px]">
          {it.earliest_label_at ? fmtShort(it.earliest_label_at) : "—"}
        </td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          <RowLinks it={it} />
        </td>
      </tr>
      {expanded && canExpand && (
        <tr className="bg-secondary/20" data-testid={`exp-row-expanded-${it.order_key}`}>
          <td colSpan={10} className="px-6 py-2">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5 flex items-center gap-1.5">
              <Package2 className="h-3 w-3" /> Colete ({it.total_parcels})
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {it.parcels.map((p) => (
                <div
                  key={p.id}
                  className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 ${
                    p.state === "picked_up" ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"
                  }`}
                  data-testid={`exp-parcel-${p.awb}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {p.state === "picked_up" ? <Truck className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <Timer className="h-3.5 w-3.5 text-amber-600 shrink-0" />}
                    <span className="font-mono text-[11px] truncate">{p.awb || "—"}</span>
                    <span className="text-[10px] text-muted-foreground">{p.carrier}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground text-right shrink-0">
                    {p.state === "picked_up" ? (
                      <>Ridicat · <span className="font-medium text-emerald-700">{p.pickup_at ? fmtShort(p.pickup_at) : ""}</span></>
                    ) : (
                      <>Așteaptă · <span className={`font-medium ${(p.waiting_hours || 0) >= 24 ? "text-rose-700" : "text-amber-700"}`}>{p.waiting_hours ? `${p.waiting_hours}h` : "—"}</span></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RowLinks({ it }) {
  const links = [];
  if (it.total_parcels > 0 && it.awb) {
    links.push(
      <Link key="delivery" to={`/livrari?awb=${encodeURIComponent(it.awb)}`} className="text-primary hover:underline text-[11px] inline-flex items-center gap-0.5" title="Vezi în Livrări">
        Livrări <ExternalLink className="h-2.5 w-2.5" />
      </Link>,
    );
  }
  if (it.shopify_order_id) {
    links.push(
      <Link key="orders" to={`/comenzi?highlight=${it.shopify_order_id}`} className="text-muted-foreground hover:text-foreground text-[11px] inline-flex items-center gap-0.5" title="Deschide comanda">
        Comandă <ChevronRight className="h-2.5 w-2.5" />
      </Link>,
    );
  }
  return <div className="flex items-center justify-end gap-2">{links}</div>;
}

// ============================== TAB 3 · Statistici ==============================
function StatisticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getExpediereStats(days);
      setData(r);
    } catch (_err) { /* silent */ } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Skeleton className="h-96 w-full" />;

  const hourly = data?.hourly_prepared || [];
  const totalHourly = hourly.reduce((s, h) => s + (h.parcels || 0), 0);
  const daily = data?.daily_comparative || [];
  const perOp = data?.per_operator_today || [];
  const rate = data?.pickup_rate_yesterday || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="exp-stats-tab">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-sky-600" /> Ritmul depozitului · Azi (colete)</div>
          <div className="text-[10px] text-muted-foreground">{totalHourly} colete pregătite</div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={hourly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="hour" fontSize={10} interval={2} tickFormatter={(h) => `${h}:00`} />
            <YAxis fontSize={10} allowDecimals={false} />
            <Tooltip labelFormatter={(h) => `${h}:00 – ${h + 1}:00`} />
            <Bar dataKey="parcels" fill="#0ea5e9" name="Colete pregătite" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Volum ultimele {days} zile</div>
          <div className="flex items-center gap-1">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`px-2 py-0.5 text-[10px] rounded-full border ${days === d ? "bg-primary text-primary-foreground border-primary" : "bg-white hover:bg-secondary/40 border-border"}`}
                data-testid={`exp-stats-range-${d}`}
              >
                {d}z
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" fontSize={9} tickFormatter={(d) => d.slice(5)} />
            <YAxis fontSize={10} allowDecimals={false} />
            <Tooltip />
            <Legend iconSize={8} wrapperStyle={{ fontSize: "10px" }} />
            <Bar dataKey="prepared_orders"  fill="#0ea5e9" name="Comenzi pregătite" radius={[3, 3, 0, 0]} />
            <Bar dataKey="prepared_parcels" fill="#38bdf8" name="Colete pregătite"  radius={[3, 3, 0, 0]} />
            <Bar dataKey="picked_parcels"   fill="#10b981" name="Colete ridicate"   radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-violet-600" /> Per operator · Azi</div>
        </div>
        {perOp.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-6">
            Niciun task de coletare finalizat azi (încă).
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="py-1.5">Operator</th>
                <th className="py-1.5 text-right">Azi</th>
                <th className="py-1.5 text-right">Media 7z</th>
                <th className="py-1.5 text-right">Δ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {perOp.map((op) => (
                <tr key={op.id} data-testid={`exp-stats-op-${op.id}`}>
                  <td className="py-1.5 font-medium">{op.name}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{op.tasks_today}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{op.avg_7d}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${op.delta_vs_avg > 0 ? "text-emerald-700" : op.delta_vs_avg < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                    {op.delta_vs_avg > 0 ? "+" : ""}{op.delta_vs_avg}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <div className="text-sm font-semibold flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-emerald-600" /> Rata de ridicare · Ieri (colete)
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-display text-5xl font-bold tabular-nums" data-testid="exp-stats-pickup-rate">
            {rate.rate_percent != null ? `${rate.rate_percent}%` : "—"}
          </div>
          {rate.rate_percent != null && rate.rate_percent < 100 && (
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
              {(rate.prepared_parcels || 0) - (rate.picked_parcels || 0)} colete rămase pe raft
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          Din {rate.prepared_parcels || 0} colete pregătite ieri, {rate.picked_parcels || 0} au fost ridicate de GLS.
        </div>
        {rate.rate_percent != null && rate.rate_percent < 90 && (
          <div className="mt-3 text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded p-2 flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Rata sub 90%. Contactează GLS pentru ridicare suplimentară sau verifică programul curierului.</span>
          </div>
        )}
      </Card>
    </div>
  );
}
